import type { Feature } from "./featureTypes";
import { SHAPE_ICONS } from "./shapeIcons";

interface FeatureTreeViewProps {
  features: Feature[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onToggle: (id: string) => void;
  onDelete: (id: string) => void;
  onMove: (id: string, direction: "up" | "down") => void;
}

export default function FeatureTreeView({
  features,
  selectedId,
  onSelect,
  onToggle,
  onDelete,
  onMove,
}: FeatureTreeViewProps) {
  if (features.length === 0) {
    return (
      <div className="feature-tree-empty">
        <span className="feature-tree-empty-text">No features yet. Add a shape to begin.</span>
      </div>
    );
  }

  return (
    <div className="feature-tree">
      {features.map((f, i) => (
        <div
          key={f.id}
          className={`feature-tree-item ${selectedId === f.id ? "selected" : ""} ${!f.enabled ? "disabled" : ""}`}
          onClick={() => onSelect(selectedId === f.id ? null : f.id)}
        >
          <span className="feature-tree-index">{i + 1}</span>
          <span className="feature-tree-icon">
            {SHAPE_ICONS[f.type] ?? null}
          </span>
          <span className="feature-tree-name">{f.name}</span>
          {selectedId === f.id && i > 0 && (
            <button
              className="feature-tree-btn feature-tree-move"
              onClick={(e) => { e.stopPropagation(); onMove(f.id, "up"); }}
              title="Move up"
            >
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M4 10l4-4 4 4" />
              </svg>
            </button>
          )}
          {selectedId === f.id && i < features.length - 1 && (
            <button
              className="feature-tree-btn feature-tree-move"
              onClick={(e) => { e.stopPropagation(); onMove(f.id, "down"); }}
              title="Move down"
            >
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M4 6l4 4 4-4" />
              </svg>
            </button>
          )}
          <button
            className="feature-tree-btn feature-tree-toggle"
            onClick={(e) => { e.stopPropagation(); onToggle(f.id); }}
            title={f.enabled ? "Disable" : "Enable"}
          >
            {f.enabled ? (
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M2 8c1.5-3 3.5-5 6-5s4.5 2 6 5c-1.5 3-3.5 5-6 5s-4.5-2-6-5z" />
                <circle cx="8" cy="8" r="2" />
              </svg>
            ) : (
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M2 8c1.5-3 3.5-5 6-5s4.5 2 6 5c-1.5 3-3.5 5-6 5s-4.5-2-6-5z" />
                <path d="M3 13L13 3" />
              </svg>
            )}
          </button>
          <button
            className="feature-tree-btn feature-tree-delete"
            onClick={(e) => { e.stopPropagation(); onDelete(f.id); }}
            title="Delete feature"
          >
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M4 4l8 8M12 4l-8 8" />
            </svg>
          </button>
        </div>
      ))}
    </div>
  );
}
